import {
  DeviceStatus,
  DeviceStatusKey,
  DeviceOperationalStatusKey,
  LatestTelemetryDevice,
  TelemetryMeasureValue,
} from '../models/dashboard.model';
import { parsePlatformTimestamp } from './company-date';

const STATE_FLAGS = [
  { bit: 0x01, key: 'hooked' },
  { bit: 0x02, key: 'unhooked' },
  { bit: 0x04, key: 'moving' },
  { bit: 0x08, key: 'stopped' },
  { bit: 0x10, key: 'fall' },
  { bit: 0x20, key: 'no-movement' },
  { bit: 0x80, key: 'sos' },
] as const;

function numericValue(measure?: TelemetryMeasureValue | null): number | null {
  if (measure?.value === null || measure?.value === undefined || measure.value === '') {
    return null;
  }
  const value = Number(measure.value);
  return Number.isFinite(value) ? value : null;
}

export function decodeStateFlags(value: unknown): string[] {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return [];
  return STATE_FLAGS.filter(flag => (numeric & flag.bit) !== 0).map(flag => flag.key);
}

export function getLatestTelemetryAt(telemetry: LatestTelemetryDevice | null): string | null {
  const timestamps = Object.values(telemetry?.measures ?? {})
    .map(measure => measure?.time ? parsePlatformTimestamp(measure.time) : Number.NaN)
    .filter(Number.isFinite);
  return timestamps.length ? new Date(Math.max(...timestamps)).toISOString() : null;
}

export function buildDeviceStatus(
  telemetry: LatestTelemetryDevice | null,
): DeviceStatus {
  const deviceState = numericValue(telemetry?.measures?.['device_state']);
  const state = numericValue(telemetry?.measures?.['state']);
  const lastTelemetryAt = getLatestTelemetryAt(telemetry);
  const flags = decodeStateFlags(state);

  let key: DeviceStatusKey = 'no-data';
  if (deviceState !== null && (deviceState & 0x01) !== 0) {
    key = 'alarm';
  } else if (deviceState !== null && (deviceState & 0x02) !== 0) {
    key = 'active';
  } else if (deviceState === 0) {
    key = 'hibernating';
  } else if (lastTelemetryAt) {
    key = 'partial';
  }

  return {
    key,
    flags,
    lastTelemetryAt,
  };
}

export function getNumericMeasure(
  telemetry: LatestTelemetryDevice | null,
  measureName: string,
): number | null {
  return numericValue(telemetry?.measures?.[measureName]);
}

export function deviceOperationalStatus(status: DeviceStatus): DeviceOperationalStatusKey {
  if (status.key === 'alarm') return 'alarm';
  if (status.key === 'no-data' || status.key === 'partial') return 'no-data';
  return status.key;
}

export function deviceOperationalRank(status: DeviceOperationalStatusKey): number {
  return ['alarm', 'no-data', 'active', 'hibernating'].indexOf(status);
}
